import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, Loader2, CheckCircle2, BadgeCheck, ImagePlus, X } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useApi, http, formatErr } from "@/lib/api";

export const COUNTRIES = { ID: "Indonesia", MY: "Malaysia", SG: "Singapore", BN: "Brunei", GB: "United Kingdom", US: "USA", AU: "Australia", NZ: "New Zealand", FR: "France", BE: "Belgique", CH: "Suisse", DE: "Deutschland", AT: "Österreich", NL: "Nederland", CA: "Canada" };
const LANG_COUNTRY = { id: "ID", ms: "MY", en: "GB", fr: "FR", de: "DE" };

function Done() {
  const { t } = useLang();
  return (
    <div data-testid="review-invite-done" className="rounded-3xl border border-emerald-200 bg-emerald-50 p-10 text-center">
      <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
      <h2 className="mt-4 text-2xl font-bold">{t("invite_done")}</h2>
      <p className="mt-2 text-slate-600">{t("invite_done_sub")}</p>
      <Link to="/" data-testid="review-invite-home-link" className="mt-6 inline-block rounded-full bg-[#064E3B] px-6 py-3 font-bold text-white">{t("nav_home")}</Link>
    </div>
  );
}

export default function ReviewInvite() {
  const { token } = useParams();
  const { t, lang, setLang } = useLang();
  const { data: inv, loading } = useApi(`/review-invite/${token}`);
  const dests = useApi("/destinations").data || [];
  const [form, setForm] = useState({ rating: 5, comment: "", destination_slug: "", country: "", offplatform_offer: false });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const files = [...e.target.files].slice(0, 3 - photos.length);
    e.target.value = "";
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await http.post(`/review-invite/${token}/photos`, fd);
        setPhotos((p) => [...p, { path: data.path, preview: URL.createObjectURL(f) }]);
      }
    } catch (err) { toast.error(formatErr(err)); } finally { setUploading(false); }
  };
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!inv) return;
    if (inv.lang && inv.lang !== lang) setLang(inv.lang);
    setForm((f) => ({ ...f, destination_slug: f.destination_slug || inv.suggested_destination || "", country: f.country || LANG_COUNTRY[inv.lang] || "ID" }));
  }, [inv]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await http.post(`/review-invite/${token}`, { ...form, rating: Number(form.rating), photos: photos.map((p) => p.path) }); setDone(true); }
    catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  if (loading) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  if (!inv) return <p data-testid="review-invite-invalid" className="py-32 text-center text-slate-500">{t("invite_invalid")}</p>;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const input = "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 outline-none focus:border-[#064E3B]";
  return (
    <div data-testid="review-invite-page" className="mx-auto max-w-xl px-4 py-12">
      {done || inv.reviewed ? <Done /> : (
        <form onSubmit={submit} className="space-y-5 rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-xl shadow-slate-200/50">
          <p className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-[#064E3B]"><BadgeCheck className="h-4 w-4" />{t("verified_booking")} · {inv.code}</p>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t("invite_title").replace("{name}", inv.contact_name)}</h1>
            <p className="mt-2 text-slate-500">{t("invite_sub")} <b className="text-slate-800">{inv.item_name}</b> · {inv.date}</p>
          </div>
          {inv.partner_name && <p data-testid="invite-partner" className="text-sm text-slate-600">{t("inv_partner")}: <b className="text-slate-900">{inv.partner_name}</b></p>}
          <div className="flex gap-1" data-testid="invite-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button type="button" key={n} data-testid={`invite-star-${n}`} onClick={() => setForm({ ...form, rating: n })}>
                <Star className={`h-9 w-9 transition-transform hover:scale-110 ${n <= form.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
              </button>
            ))}
          </div>
          <select data-testid="invite-destination-select" required value={form.destination_slug} onChange={set("destination_slug")} className={input}>
            <option value="">{t("invite_place")}</option>
            {dests.map((d) => <option key={d.slug} value={d.slug}>{d.name}</option>)}
          </select>
          <select data-testid="invite-country-select" value={form.country} onChange={set("country")} className={input}>
            {Object.entries(COUNTRIES).map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </select>
          <textarea data-testid="invite-comment-input" required minLength={3} rows={5} value={form.comment} onChange={set("comment")} placeholder={t("review_ph")} className={input} />
          <div data-testid="invite-photos" className="flex flex-wrap gap-3">
            {photos.map((p, i) => (
              <div key={p.path} className="relative h-20 w-20 overflow-hidden rounded-2xl ring-1 ring-slate-200">
                <img src={p.preview} alt="" className="h-full w-full object-cover" />
                <button type="button" data-testid={`invite-photo-remove-${i}`} onClick={() => setPhotos(photos.filter((x) => x.path !== p.path))} className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            {photos.length < 3 && (
              <label data-testid="invite-photo-upload" className="flex h-20 cursor-pointer items-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 px-4 text-sm font-semibold text-slate-600 transition-colors hover:border-[#064E3B] hover:text-[#064E3B]">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}{uploading ? t("uploading") : t("add_photos")}
                <input data-testid="invite-photo-input" type="file" accept="image/jpeg,image/png,image/webp" multiple hidden disabled={uploading} onChange={upload} />
              </label>
            )}
          </div>
          {inv.partner_name && (
            <label data-testid="invite-report" className="flex gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm">
              <input type="checkbox" checked={form.offplatform_offer} onChange={(e) => setForm({ ...form, offplatform_offer: e.target.checked })} data-testid="invite-report-checkbox" className="mt-0.5 h-4 w-4 shrink-0 accent-[#E11D48]" />
              <span><span className="font-semibold text-slate-800">{t("inv_report").replace("{name}", inv.partner_name)}</span><span className="mt-0.5 block text-xs text-slate-500">{t("inv_report_hint")}</span></span>
            </label>
          )}
          <button disabled={busy || uploading} data-testid="invite-submit-btn" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#E11D48] py-4 font-bold text-white transition-colors hover:bg-[#BE123C] disabled:opacity-60">
            {busy && <Loader2 className="h-5 w-5 animate-spin" />}{t("submit_review")}
          </button>
        </form>
      )}
    </div>
  );
}
